<template>
	<div tabindex="0" class="hide-outline">
		<main v-if="loaded" class="main-view no-top-panel">
			<div class="is-flex is-justify-content-space-between">
				<div class="title">{{ round.name }}</div>
				<div class="buttons has-addons is-flex is-justify-content-flex-end">
					<button
						class="button is-success is-half"
						@click="finishRound"
						:disabled="disableDone"
						:class="saving ? 'is-loading' : ''"
					>
						<span class="icon is-white"><i class="fas fa-clipboard-check"/></span>
						<span>Slutför rond</span>
					</button>
					<button
						class="button is-half"
						@click="pauseRound"
						:class="saving ? 'is-loading' : ''"
					>
						<span class="icon"><i class="fas fa-pause"/></span>
						<span>Pausa rond</span>
					</button>
				</div>
			</div>

			<TabComponent :tabs="tabs" v-model="selectedTab" :fullwidth="isMobile" />
			<MapComponent
				:map="round.map"
				:infos="round.infos"
				v-if="selectedTab === 0"
				@addFlaw="addFlaw"
				@saveState="saveState"
				@update="loadState"
				ref="mapview"
			/>
			<ProtectionsList
				:infos="round.infos"
				v-if="selectedTab === 1"
				@addFlaw="addFlaw"
				@saveState="saveState"
				@update="changeInfo"
				ref="protectionlist"
			/>
			<Checklist
				:checklists="round.checklists"
				v-if="selectedTab === 2"
				@saveState="saveState"
			/>
			<SummaryComponent
				v-if="selectedTab === 3"
				:infos="round.infos"
				:checklists="round.checklists"
				:success="success"
				:saving="saving"
				@allOk="setAllOk"
				@pauseRound="pauseRound"
				@finishRound="finishRound"
			/>
			<AddFlawModal
				v-if="showFlawModal"
				@cancel="toggleFlawModal"
				@flaw-added="flawAdded"
				@flaw-fixed="setFixed"
				@continueWithoutAdding="continueWithoutAddingFlaw"
			/>
		</main>
	</div>
</template>

<script>
import { mapGetters, mapActions, mapState, mapMutations } from 'vuex';
import TabComponent from '@/components/TabComponent';
import MapComponent from './components/Map';
import ProtectionsList from './components/ProtectionsList';
import Checklist from './components/Checklist';
import SummaryComponent from './components/Summary';
import AddFlawModal from './modals/AddFlawModal.vue';

export default {
	components: {
		TabComponent,
		MapComponent,
		ProtectionsList,
		Checklist,
		SummaryComponent,
		AddFlawModal
	},
	data: () => ({
		selectedTab: 1,
		roundData: null,
		tabs: [
			{
				name: 'Ritning',
				icon: 'fa-map',
				disabled: true
			},
			{
				name: 'Lista',
				icon: 'fa-list',
				disabled: false
			},
			{
				name: 'Frågor',
				icon: 'fa-clipboard-list',
				disabled: false
			},
			{
				name: 'Sammanställning',
				icon: 'fa-chart-bar',
				disabled: false
			}
		],
		loaded: false,
		success: null,
		saving: false,
		isDone: false,
		showFlawModal: false
	}),
	computed: {
		...mapGetters('roundStore', ['round']),
		...mapGetters('appStore', ['isMobile']),
		...mapGetters('roundHistoryStore', ['activeRound']),
		...mapGetters('roundHistoryInfoStore', ['roundHistoryInfos']),
		...mapState('roundHistoryInfoStore', {
			infoParams: 'searchParams'
		}),
		...mapGetters('roundHistoryQuestionStore', ['roundHistoryQuestionLists']),
		...mapState('roundHistoryQuestionStore', {
			questionParams: 'searchParams'
		}),
		...mapState('fileStore', ['file']),
		...mapGetters('user', ['getUser']),
		mobile() {
			return this.isMobile ? { display: 'block' } : '';
		},

		totProtections() {
			let list = [];
			this.round.infos.forEach(info => {
				info.roundHistoryItems.forEach(item => {
					list.push(item);
				});
			});

			return list;
		},

		checkedItems() {
			let list = [];
			this.totProtections.forEach(item => {
				if (item.controlled || item.continueWithoutAdding) {
					list.push(item);
				}
			});

			return list;
		},

		questionData() {
			let data = [0, 0, 0];
			this.questions.forEach(question => {
				if (question.answer === question.positive || question.answer === '') {
					data[0]++;
				} else if (question.answer === question.negative) {
					data[1]++;
				} else {
					data[2]++;
				}
			});

			return data;
		},

		questions() {
			let list = [];
			this.round.checklists.forEach(checklist => {
				checklist.roundHistoryQuestions.forEach(question => {
					list.push(question);
				});
			});

			return list;
		},

		disableDone() {
			const items = this.totProtections.length - this.checkedItems.length;
			const questions = this.questionData[0] + this.questionData[1] - this.questions.length;

			return items !== 0 || questions !== 0;
		}
	},
	watch: {
		selectedTab(to) {
			this.roundData.currentTab = to;
			this.saveState();
			this.bulkSave();
			var viewport = document.querySelector('meta[name="viewport"]');

			if (viewport) {
				viewport.content = 'initial-scale=1';
				viewport.content = `width=${window.innerWidth}`;
			}
		}
	},
	methods: {
		...mapMutations('appStore', ['SET_COLLAPSED']),
		...mapMutations('fileStore', ['AddRoundImage', 'ResetRoundImages']),
		...mapActions('roundStore', ['setRound']),
		...mapActions('roundHistoryQuestionStore', [
			'updateRoundHistoryQuestion',
			'getRoundHistoryQuestionLists'
		]),
		...mapActions('roundHistoryItemStore', ['updateRoundHistoryItem']),
		...mapActions('roundHistoryInfoStore', ['getRoundHistoryInfos']),
		...mapActions('flawStore', ['createFlaw', 'updateFlaw']),
		...mapActions('roundHistoryStore', ['updateRoundHistory']),
		...mapActions('fileStore', ['saveFile', 'getFile']),
		...mapMutations('roundHistoryStore', ['SET_ACTIVEROUND']),
		toggleFlawModal() {
			this.showFlawModal = !this.showFlawModal;
		},
		setFixed(item) {
			this.loadState();
			this.selectedTab === 0
				? this.$refs.mapview.flawAdded(item)
				: this.$refs.protectionlist.flawAdded(item);
		},
		flawAdded(item) {
			this.loadState();
			this.selectedTab === 0
				? this.$refs.mapview.flawAdded(item)
				: this.$refs.protectionlist.flawAdded(item);
			this.showFlawModal = false;
		},
		addFlaw(index, position) {
			this.roundData = { ...this.activeRound };

			if (position) {
				this.roundData.position = position;
			}

			this.roundData.flawIndex = index;
			this.saveState();
			this.showFlawModal = true;
		},
		continueWithoutAddingFlaw(item) {
			let thisInfo = this.round.infos.find(x => x.roundHistoryItems.some(y => y.id === item.id));
			let thisItem = thisInfo.roundHistoryItems.find(x => x.id === item.id);

			thisItem.continueWithoutAdding = true;
			thisItem.controlled = this.setDate();

			this.showFlawModal = false;

			this.$refs?.protectionlist?.$forceUpdate();
			this.saveState();
		},
		saveState() {
			this.SET_ACTIVEROUND(this.roundData);
		},
		loadState() {
			this.roundData = { ...this.activeRound };
			this.setRound(this.roundData);
		},
		saveLoad() {
			this.saveState();
			this.$nextTick(() => {
				this.roundData = { ...this.activeRound };
				this.setRound(this.roundData);
			});
		},

		changeInfo() {
			this.roundData = { ...this.activeRound };
			this.bulkSave();
		},

		async asyncForEach(array, callback) {
			for (let index = 0; index < array.length; index++) {
				await callback(array[index], index, array);
			}
		},

		async bulkSave() {
			if (this.saving === true) return;

			const round = this.roundData;

			this.saving = true;
			this.success = null;
			this.questionParams.pageSize = 0;
			this.infoParams.pageSize = 0;
			this.questionParams.roundHistoryId = round.id;
			this.infoParams.roundHistoryId = round.id;

			if (round.checklists) {
				await this.asyncForEach(
					round.checklists,
					async(checklist, checkIndex) => {
						await this.asyncForEach(checklist.roundHistoryQuestions, question => {
							const qId = round.oldChecklists[
								checkIndex
							].roundHistoryQuestions.findIndex(q => q.id === question.id);

							if (question.answer !==round.oldChecklists[checkIndex].roundHistoryQuestions[qId].answer && this.success !== false) {
								question.roundHistoryQuestionListId = checklist.id;
								this.updateRoundHistoryQuestion(question).catch(() => {
									this.success = false;
								});
							}
						}
						);
					}
				);
				this.getRoundHistoryQuestionLists().then(() => {
					this.roundData.oldChecklists = this.roundHistoryQuestionLists;
					this.setRound(this.roundData);
				});
			}

			function urltoFile(url, filename, mimeType) {
				return fetch(url)
					.then(function(res) {
						return res.arrayBuffer();
					})
					.then(function(buf) {
						return new File([buf], filename, { type: mimeType });
					});
			}

			await this.asyncForEach(round.infos, async(info, infoIndex) => {
				await this.asyncForEach(info.roundHistoryItems, async(item, index) => {
					item.roundHistoryInfoId = info.id;

					if (item.id) {
						let compare = item.controlled;

						if (compare && (typeof compare === 'string' || typeof compare === 'object')) {
							if (typeof compare === 'object') compare = compare.toISOString();

							if (compare.includes('Z'))
								compare = compare.slice(0, compare.length - 1);
						}

						if (compare !== round.oldInfos[infoIndex].roundHistoryItems[index].controlled && this.success !== false) {
							this.updateRoundHistoryItem(item).catch(() => {
								this.success = false;
							});
						}

						if (item.flaws.length) {
							await this.asyncForEach(item.flaws, (flaw, flawIndex) => {
								const oldFlaw = round.oldInfos[infoIndex].roundHistoryItems[index].flaws[flawIndex];
								const oldState = oldFlaw ? oldFlaw.done : false;

								if (flaw.id && flaw.done !== oldState && this.success !== false) {
									this.updateFlaw(flaw).catch(() => {
										this.success = false;
									});
								} else if (!flaw.id) {
									this.createFlaw(flaw)
										.then(async data => {
											flaw.id = data.id;

											if (flaw.file) {
												const fileBlob = await urltoFile(
													flaw.file,
													'flaw',
													flaw.file.slice(5).split(';')[0]
												);

												const file = { file: fileBlob, flawId: flaw.id };
												this.saveFile(file);
											}
										})
										.catch(() => {
											this.success = false;
										});
								}
							});
						}
					}
				});

				if (info.flaws) {
					await this.asyncForEach(info.flaws, (flaw, index) => {
						const done = round.oldInfos[infoIndex].flaws.length > index
							? round.oldInfos[infoIndex].flaws[index].done
							: false;
						const flawScale = round.oldInfos[infoIndex].flaws.length > index
							? round.oldInfos[infoIndex].flaws[index].flawScale
							: false;

						if (flaw.id && (flaw.done !== done || flaw.flawScale !== flawScale) && this.success !== false) {
							this.updateFlaw(flaw).catch(() => {
								this.success = false;
							});
						} else if (!flaw.id) {
							this.createFlaw(flaw)
								.then(async data => {
									flaw.id = data.id;

									if (flaw.file) {
										const fileBlob = await urltoFile(
											flaw.file,
											'flaw',
											flaw.file.slice(5).split(';')[0]
										);

										const file = { file: fileBlob, flawId: flaw.id };
										this.saveFile(file);
									}
								})
								.catch(() => {
									this.success = false;
								});
						}
					});
				}
			});

			if (this.success !== false) {
				await this.getRoundHistoryInfos()
					.then(() => {
						let infoList = [];
						this.roundHistoryInfos.forEach(info => {
							info.roundHistoryItems.sort((a, b) =>
								a.positionNumber > b.positionNumber
									? 1
									: a.positionNumber === b.positionNumber
										? 0
										: -1
							);
							infoList.push(info);
						});
						this.roundData.oldInfos = infoList;
					})
					.catch(() => {
						this.success = false;
					});
			}

			await this.updateRoundHistory(this.roundData)
				.then(() => {
					this.setRound(this.roundData);
					this.success = true;
				})
				.catch(() => {
					this.success = false;
				});
			this.saving = false;
		},
		setAllOk() {
			this.roundData.infos.forEach(info => {
				info.roundHistoryItems.forEach(item => {
					if (!item.controlled) {
						item.controlled = this.setDate();
						item.userName = this.getUser.email;
					}

					if (item.flaws) {
						item.flaws.forEach(flaw => {
							if (!flaw.done && flaw.created && flaw.created < this.round.created) {
								flaw.done = this.setDate();
								flaw.doneBy = this.getUser.email;
							}
						});
					}
				});
			});
			this.bulkSave();
		},
		async pauseRound() {
			await this.bulkSave();

			if (this.success) this.toDashboard();
		},
		async finishRound() {
			this.roundData.done = this.setDate();
			await this.bulkSave();
			this.isDone = true;

			if (this.success) this.toDashboard();
		},
		toDashboard() {
			this.$router.push({
				name: 'Dashboard'
			});
		},
		setDate() {
			let date = new Date();
			date.setHours(date.getHours() - date.getTimezoneOffset() / 60);

			return date;
		},
		setRoundDone() {
			this.isDone = true;
		},
		beforeWindowUnload(e) {
			if (this.confirmLeaveRound()) {
				e.preventDefault();
				e.returnValue = '';
			}
		},
		confirmLeave() {
			return window.confirm('Vill du verkligen lämna den påbörjade ronden?');
		},
		confirmLeaveRound() {
			return !this.isDone && !this.confirmLeave();
		}
	},
	async created() {
		const isOnIOS = navigator.userAgent.match(/iPad/i) || navigator.userAgent.match(/iPhone/i);
		const eventName = isOnIOS ? 'pagehide' : 'beforeunload';
		window.addEventListener(eventName, this.beforeWindowUnload);

		if (!this.isMobile) this.SET_COLLAPSED(true);

		if (this.activeRound) {
			this.roundData = { ...this.activeRound };
			this.setRound(this.roundData);

			if (this.roundData.currentInfo || this.roundData.currentInfo === 0) {
				this.loaded = true;
			}

			if (!this.roundData.checklists || !this.roundData.checklists.length) {
				this.tabs[2].disabled = true;
			}

			this.roundData.currentTab = this.roundData.currentTab || this.roundData.currentTab === 0 ? this.roundData.currentTab : 1;
			this.selectedTab = this.roundData.currentTab;

			if (!this.roundData.currentInfo && this.roundData.currentInfo !== 0) {
				this.roundData.currentInfo = 0;
			}

			this.ResetRoundImages();
			await this.asyncForEach(this.roundData.infos, async info => {
				if (info.fileId) {
					await this.getFile(info.fileId).then(() => {
						this.AddRoundImage({ fileId: info.fileId, img: this.file });
						// this.roundData.round.infos[index].map = this.file;
						this.tabs[0].disabled = false;
					});
				}
			});
			this.saveState();
			this.loaded = true;
		}
	},

	beforeDestroy() {
		const isOnIOS = navigator.userAgent.match(/iPad/i) || navigator.userAgent.match(/iPhone/i);
		const eventName = isOnIOS ? 'pagehide' : 'beforeunload';
		window.removeEventListener(eventName, this.beforeWindowUnload);
		this.bulkSave();
		this.SET_COLLAPSED(false);
		this.ResetRoundImages();
	},

	beforeRouteLeave(to, from, next) {
		if (this.confirmLeaveRound()) {
			next(false);
		} else {
			next();
		}
	}
};
</script>

<style lang="scss" scoped>
@import "@/assets/colors.scss";

.top-box {
  background: $lightest-grey;
  padding: 10px 5px 10px 5px;
  display: block;
  border-radius: 2px;
  margin-bottom: 10px;
  .row {
    display: flex;
  }
  .field {
    &.name {
      flex: 1;
    }
    &.description {
      flex: 2;
    }
    margin-left: 10px;
    margin-right: 10px;
    .label {
      font-size: 12px;
    }
  }
  .textarea,
  .input {
    background: white;
    border-radius: 2px;
  }
  .buttons {
    margin: 0;
    margin-top: 19px;
    margin-left: 10px;
    .button {
      border-radius: 2px;
      width: 110px;
    }
  }
}
.tabs.is-boxed {
  margin-bottom: 0;
}
</style>
